import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { SidebarProvider, SidebarInset } from '@/components/ui/sidebar';
import AppSidebar from '@/components/layout/AppSidebar';
import Header from '@/components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Plus,
  Search,
  Star,
  Upload,
  Eye,
  EyeOff,
  Layers,
} from 'lucide-react';
import { useMaterials, DatabaseMaterial, MaterialCategory } from '@/hooks/useMaterials';
import { useFavoriteMaterials } from '@/hooks/useFavoriteMaterials';
import { useHiddenMaterials } from '@/hooks/useHiddenMaterials';
import { groupByFormat } from '@/components/configurator-v2/material-picker/dedupByFormat';
import ImportCustomizationsWizard from '@/components/materials/ImportCustomizationsWizard';
import { useCurrentOrganization } from '@/contexts/OrganizationContext';
import { facetsFor, matchesFacets, type Facet } from '@/lib/materials/facets';
import { useMaterialsUsage } from '@/hooks/useMaterialsUsage';
import MaterialsTable from '@/components/materials/MaterialsTable';
import CreateMaterialDialog from '@/components/materials/CreateMaterialDialog';
import EditMaterialDialog from '@/components/materials/EditMaterialDialog';
import BulkPriceEditDialog from '@/components/materials/BulkPriceEditDialog';
import { TrendingUp, X as XIcon } from 'lucide-react';
import { ChevronDown } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { exportMaterialsToExcel } from '@/utils/export/exportMaterialsExcel';
import { useWasteFactors } from '@/hooks/useWasteFactors';
import { parseMaterialsExcel, ParsedMaterialInput } from '@/utils/import/importMaterialsExcel';
import { toast } from 'sonner';
import { supabase } from "@/integrations/supabase/client";
import { useMaterialsSorting } from '@/hooks/useMaterialsSorting';
import { formatNumber } from '@/utils/format';

type CategoryFilter = MaterialCategory | 'all' | 'unknown';

// Separare la lista delle categorie in un modulo helper
const materialCategoryOptions: { value: CategoryFilter; label: string }[] = [
  { value: 'all', label: 'Tutte le categorie' },
  { value: 'board', label: 'Lastre' },
  { value: 'ceiling_tile', label: 'Pannelli controsoffitto' },
  { value: 'structure_frame', label: 'Struttura - Montanti' },
  { value: 'structure_guide', label: 'Struttura - Guide' },
  { value: 'insulation', label: 'Isolanti' },
  { value: 'accessory', label: 'Accessori' },
  { value: 'screw', label: 'Viti' },
  { value: 'finish', label: 'Finiture (intonaci/rasanti)' },
  { value: 'other', label: 'Altro' },
  { value: 'unknown', label: 'Sconosciuta / vuota' },
];

// allowed categories for import:
const allowedCategories = [
  'board', 'ceiling_tile', 'structure_frame', 'structure_guide',
  'insulation', 'accessory', 'screw', 'finish', 'other'
] as const;

const VALID_CATEGORIES = new Set<string>([
  'board', 'ceiling_tile', 'structure_frame', 'structure_guide',
  'insulation', 'accessory', 'screw', 'finish', 'other',
]);

// helper filtraggio materiali (estraibile volendo)
const filterMaterials = (
  materials: DatabaseMaterial[],
  searchTerm: string,
  selectedCategory: CategoryFilter,
  selectedSupplier: string | 'all'
): DatabaseMaterial[] => {
  const q = searchTerm.trim().toLowerCase();
  const supplierTrim = (selectedSupplier ?? '').trim();
  return materials.filter((material: DatabaseMaterial) => {
    // Search: solo nome e codice (NON supplier — quello ha già il suo dropdown).
    // Cercare nel supplier rende confuso: scrivi "saint" e vedi tutto SG → meglio usare il filtro.
    const matchesSearch = !q ||
      material.name.toLowerCase().includes(q) ||
      material.code.toLowerCase().includes(q);
    const matchesCategory =
      selectedCategory === 'all' ||
      (selectedCategory === 'unknown'
        ? !material.category || !VALID_CATEGORIES.has(material.category as string)
        : material.category === selectedCategory);
    // Trim difensivo per evitare drift su whitespace
    const matchesSupplier =
      supplierTrim === 'all' || supplierTrim === '' ||
      (material.supplier ?? '').trim() === supplierTrim;
    return matchesSearch && matchesCategory && matchesSupplier;
  });
};

const Stat: React.FC<{ label: string; value: React.ReactNode; accent?: string }> = ({ label, value, accent = '' }) => (
  <div>
    <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
    <div className={`text-xl font-bold ${accent}`}>{value}</div>
  </div>
);

const Materials = () => {
  const [searchTerm, setSearchTerm] = useState('');
  // Aggiunto 'unknown' per filtrare materiali con categoria mancante/non standard
  const [selectedCategory, setSelectedCategory] = useState<CategoryFilter>('all');
  const [selectedSupplier, setSelectedSupplier] = useState<string | 'all'>('all');
  /** Faccetta → valori selezionati. Stesse faccette del picker (lib/materials/facets). */
  const [activeFacets, setActiveFacets] = useState<Record<string, string[]>>({});
  const { usageMap, isReady: usageReady } = useMaterialsUsage();
  // Scope: globale = materiali catalogo (organization_id NULL), mio = materiali
  // della org corrente (organization_id = currentOrg). Default 'all' = entrambi.
  // Utile per chi vuole vedere solo i suoi materiali custom, o solo il catalogo
  // da cui clonare in privato.
  const [selectedScope, setSelectedScope] = useState<'all' | 'global' | 'mine'>('all');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState<any>(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importPreview, setImportPreview] = useState<ParsedMaterialInput[]>([]);
  const [importErrors, setImportErrors] = useState<string[]>([]);
  const [importing, setImporting] = useState(false);
  const [onlyFavorites, setOnlyFavorites] = useState(false);
  const [showHidden, setShowHidden] = useState(false);
  // "Raggruppa per formato": collassa le varianti che differiscono solo per la
  // lunghezza (stesso prezzo €/mq o €/ml) in una riga sola. Riusa la stessa
  // logica del configuratore (dedupByFormat.ts). Le varianti NON vengono
  // eliminate: il codice esatto resta per la distinta materiali / ordine.
  const [groupByFormatOn, setGroupByFormatOn] = useState(false);
  const [customWizardOpen, setCustomWizardOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  // Paginazione: limitiamo il render della tabella a PAGE_SIZE righe per pagina.
  // 2770 righe nel DOM = pagina lenta a interagire (DOM enorme, layout reflow,
  // listener event massicci). 50 righe = sufficiente per uno screen, cap render.
  const PAGE_SIZE = 50;
  const [pageIndex, setPageIndex] = useState(0);

  const { data: materials = [], isLoading, refetch } = useMaterials();
  const { favoritesSet } = useFavoriteMaterials();
  const { hiddenSet } = useHiddenMaterials();
  const { currentOrganizationId } = useCurrentOrganization();

  // Lista fornitori unica per il filtro (derivata dai materiali stessi)
  const supplierOptions = React.useMemo(() => {
    const set = new Set<string>();
    for (const m of materials) {
      if (m.supplier && m.supplier.trim()) set.add(m.supplier.trim());
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [materials]);

  // Memoizziamo filter+favorites: senza memo l'IIFE rigira su tutte le 2770
  // righe a ogni keystroke / hover / state change esterno, anche se search
  // e filtri non sono cambiati. Con useMemo si ricomputa solo quando una
  // delle dipendenze cambia davvero.
  /**
   * Faccette della categoria + quella trasversale "Utilizzato".
   *
   * L'utilizzo non sta in `lib/materials/facets` perche' dipende da un dato che
   * solo questa pagina carica (il conteggio su tutta la piattaforma): il picker
   * del configuratore non ne ha bisogno e non deve pagarne la query.
   */
  const facets = React.useMemo<Facet[]>(() => {
    const base = facetsFor(selectedCategory);
    if (!usageReady) return base;
    const usoFacet: Facet = {
      key: 'utilizzo',
      label: 'Utilizzato',
      options: [
        { value: 'si', label: 'Sì' },
        { value: 'no', label: 'Mai usato' },
      ],
      valuesOf: (m) => [(usageMap.get(m.id) ?? 0) > 0 ? 'si' : 'no'],
    };
    return [...base, usoFacet];
  }, [selectedCategory, usageMap, usageReady]);

  const filteredMaterials = React.useMemo(() => {
    let base = filterMaterials(materials, searchTerm, selectedCategory, selectedSupplier);
    // Filtro scope: globale (org_id NULL) vs mio (org_id == currentOrg).
    if (selectedScope === 'global') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      base = (base as any[]).filter(m => (m as { organization_id?: string | null }).organization_id == null);
    } else if (selectedScope === 'mine' && currentOrganizationId) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      base = (base as any[]).filter(m => (m as { organization_id?: string | null }).organization_id === currentOrganizationId);
    }
    if (onlyFavorites) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      base = (base as any[]).filter(m => favoritesSet.has(m.id));
    }
    // Nascosti: di default esclusi dalla vista. Con "Nascosti" attivo mostra
    // SOLO i nascosti (come "Solo preferiti") → facili da trovare e riattivare.
    if (hiddenSet.size > 0) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      base = (base as any[]).filter(m => showHidden ? hiddenSet.has(m.id) : !hiddenSet.has(m.id));
    }
    // Faccette per categoria (rivestimento, lamierino, larghezza, proprieta'...).
    if (Object.keys(activeFacets).length > 0) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      base = (base as any[]).filter(m => matchesFacets(m, facets, activeFacets));
    }
    return base;
  }, [materials, searchTerm, selectedCategory, selectedSupplier, selectedScope, currentOrganizationId, onlyFavorites, favoritesSet, showHidden, hiddenSet, activeFacets, facets]);


  /**
   * Base su cui contare i chip: tutti i filtri tranne le faccette.
   * Il numero accanto a un chip dice quanti materiali resterebbero cliccandolo.
   */
  const facetCountBase = React.useMemo(() => {
    let base = filterMaterials(materials, searchTerm, selectedCategory, selectedSupplier);
    if (selectedScope === 'global') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      base = (base as any[]).filter(m => (m as { organization_id?: string | null }).organization_id == null);
    } else if (selectedScope === 'mine' && currentOrganizationId) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      base = (base as any[]).filter(m => (m as { organization_id?: string | null }).organization_id === currentOrganizationId);
    }
    if (onlyFavorites) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      base = (base as any[]).filter(m => favoritesSet.has(m.id));
    }
    if (hiddenSet.size > 0) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      base = (base as any[]).filter(m => showHidden ? hiddenSet.has(m.id) : !hiddenSet.has(m.id));
    }
    return base;
  }, [materials, searchTerm, selectedCategory, selectedSupplier, selectedScope, currentOrganizationId, onlyFavorites, favoritesSet, showHidden, hiddenSet]);

  const toggleFacetValue = (facetKey: string, value: string) => {
    setActiveFacets(prev => {
      const current = prev[facetKey] ?? [];
      const next = current.includes(value) ? current.filter(v => v !== value) : [...current, value];
      const out = { ...prev };
      if (next.length === 0) delete out[facetKey];
      else out[facetKey] = next;
      return out;
    });
  };

  // Conteggio nascosti tra i materiali attualmente caricati (per il toggle).
  const hiddenCount = React.useMemo(
    () => materials.reduce((n, m) => (hiddenSet.has(m.id) ? n + 1 : n), 0),
    [materials, hiddenSet],
  );

  // ---- ORDINAMENTO ----
  const {
    sortField,
    sortDirection,
    setSort,
    getSortedMaterials,
  } = useMaterialsSorting('name', 'asc');
  // Anche il sort va memoizzato: getSortedMaterials e' una funzione che
  // riordina N elementi (~2770), se rigirata ad ogni render diventa il
  // secondo collo di bottiglia dopo filteredMaterials.
  const sortedMaterials = React.useMemo(
    () => getSortedMaterials(filteredMaterials),
    [getSortedMaterials, filteredMaterials, sortField, sortDirection],
  );

  // Raggruppamento per formato (opzionale): quando attivo, la lista mostra un
  // solo rappresentante per gruppo + una mappa id→etichette formato per il badge.
  const { displayMaterials, formatGroups } = React.useMemo(() => {
    if (!groupByFormatOn) {
      return { displayMaterials: sortedMaterials, formatGroups: undefined as Map<string, string[]> | undefined };
    }
    const groups = groupByFormat(sortedMaterials);
    const fmt = new Map<string, string[]>();
    for (const g of groups) {
      if (g.formats.length > 1) fmt.set(g.representative.id, g.formats);
    }
    return { displayMaterials: groups.map(g => g.representative), formatGroups: fmt };
  }, [sortedMaterials, groupByFormatOn]);

  // Reset alla prima pagina quando cambiano filtri o sort (altrimenti rischi
  // di stare sulla pagina 12 ma con 20 risultati totali → schermo vuoto).
  React.useEffect(() => {
    setPageIndex(0);
  }, [searchTerm, selectedCategory, selectedSupplier, selectedScope, onlyFavorites, groupByFormatOn, sortField, sortDirection]);

  // Slice della pagina corrente: e' questo l'array che renderizza la tabella.
  // Memoizzato per stabilita' del riferimento (evita re-render figli inutili).
  const totalPages = Math.max(1, Math.ceil(displayMaterials.length / PAGE_SIZE));
  const safePageIndex = Math.min(pageIndex, totalPages - 1);
  const pagedMaterials = React.useMemo(() => {
    const start = safePageIndex * PAGE_SIZE;
    return displayMaterials.slice(start, start + PAGE_SIZE);
  }, [displayMaterials, safePageIndex]);

  const handleMaterialCreated = () => {
    refetch();
  };

  const handleMaterialUpdated = () => {
    refetch();
  };

  const { wasteMap } = useWasteFactors();
  const handleExportVisible = () => {
    if (filteredMaterials.length === 0) {
      toast.error('Nessun materiale visibile da esportare.');
      return;
    }
    exportMaterialsToExcel(filteredMaterials, wasteMap);
    toast.success(`Esportati ${filteredMaterials.length} materiali (visibili).`);
  };
  const handleExportSelected = () => {
    if (selectedIds.size === 0) {
      toast.error('Nessun materiale selezionato.');
      return;
    }
    const subset = materials.filter((m) => selectedIds.has(m.id));
    exportMaterialsToExcel(subset, wasteMap);
    toast.success(`Esportati ${subset.length} materiali (selezionati).`);
  };
  const handleExportAll = () => {
    if (materials.length === 0) {
      toast.error('Catalogo vuoto.');
      return;
    }
    exportMaterialsToExcel(materials, wasteMap);
    toast.success(`Esportati ${materials.length} materiali (tutti).`);
  };

  const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const { materials, errors } = await parseMaterialsExcel(file);
    // Filtra solo materiali con categoria valida, ora incluye anche 'other'
    const validMaterials = materials.filter(m =>
      allowedCategories.includes(m.category as MaterialCategory)
    ).map(m => ({
      ...m,
      category: m.category as MaterialCategory
    }));
    const invalidRows = materials
      .filter(m => !allowedCategories.includes(m.category as MaterialCategory))
      .map(m => `Codice ${m.code}: categoria non valida "${m.category}"`);
    setImportPreview(validMaterials);
    setImportErrors([...errors, ...invalidRows]);
  };

  const handleSaveImport = async () => {
    setImporting(true);
    let ok = 0, fail = 0, familyResolved = 0, familyMissing = 0;

    // Pre-fetch fornitori + famiglie per risolvere (supplier_name + family_code) → family_id
    const { data: suppliersList } = await supabase
      .from('suppliers')
      .select('id, name');
    const { data: familiesList } = await supabase
      .from('supplier_product_families')
      .select('id, supplier_id, code');
    const supplierByName = new Map<string, string>();
    (suppliersList ?? []).forEach((s: { id: string; name: string }) =>
      supplierByName.set(s.name.trim().toLowerCase(), s.id),
    );
    const familyByKey = new Map<string, string>();
    (familiesList ?? []).forEach((f: { id: string; supplier_id: string; code: string }) =>
      familyByKey.set(`${f.supplier_id}::${(f.code || '').trim().toLowerCase()}`, f.id),
    );

    for (const m of importPreview) {
      if (!allowedCategories.includes(m.category as MaterialCategory)) {
        fail++;
        continue;
      }

      // Resolve family_id da (supplier, family_code)
      let resolvedFamilyId: string | null = null;
      if (m.family_code) {
        const supId = supplierByName.get(m.supplier.trim().toLowerCase());
        if (supId) {
          resolvedFamilyId = familyByKey.get(`${supId}::${m.family_code.trim().toLowerCase()}`) ?? null;
        }
        if (resolvedFamilyId) familyResolved++;
        else familyMissing++;
      }

      // Costruisci payload con TUTTI i campi (null = clear). undefined → omesso da spread.
      const inputMaterial = {
        code: m.code,
        name: m.name,
        category: m.category as MaterialCategory,
        supplier: m.supplier,
        family_code: m.family_code ?? null,
        family_id: resolvedFamilyId,
        ean_code: m.ean_code ?? null,
        description: m.description ?? null,

        thickness: m.thickness ?? null,
        is_variable_thickness: m.is_variable_thickness ?? null,
        width: m.width ?? null,
        length: m.length ?? null,
        weight_per_sqm: m.weight_per_sqm ?? null,
        weight_per_ml: m.weight_per_ml ?? null,
        sheet_thickness: m.sheet_thickness ?? null,
        density: m.density ?? null,

        list_price: m.list_price ?? null,
        unit_price: m.unit_price,
        unit: m.unit,
        discount: m.discount != null ? String(m.discount) : null,
        extra_discount: m.extra_discount ?? null,
        delivery_indicator: m.delivery_indicator ?? null,
        valid_until: m.valid_until ?? null,

        incidence_base: m.incidence_base ?? null,
        incidence_per_sqm: m.incidence_per_sqm ?? null,
        passo: m.passo ?? null,
        installation_time_per_sqm: m.installation_time_per_sqm ?? null,
        box_pieces: m.box_pieces ?? null,
        waste_percentage: m.waste_percentage ?? null,
        disposal_percentage: m.disposal_percentage ?? null,
        installation_notes: m.installation_notes ?? null,

        material_type: m.material_type ?? null,
        board_typology: m.board_typology ?? null,
        board_type: m.board_type ?? null,
        flexural_strength: m.flexural_strength ?? null,
        surface_hardness: m.surface_hardness ?? null,
        en_520_type: m.en_520_type ?? null,
        water_absorption: m.water_absorption ?? null,
        humidity_resistance_class: m.humidity_resistance_class ?? null,
        thermal_conductivity: m.thermal_conductivity ?? null,
        acoustic_performance: m.acoustic_performance ?? null,

        fire_resistance_class: m.fire_resistance_class ?? null,
        fire_class: m.fire_class ?? null,
        fire_description: m.fire_description ?? null,
        fire_usage_notes: m.fire_usage_notes ?? null,
        fire_performance_notes: m.fire_performance_notes ?? null,
        rei_compatible: m.rei_compatible ?? null,

        profile_type: m.profile_type ?? null,
        surface_finish: m.surface_finish ?? null,

        environmental_certification: m.environmental_certification ?? null,
        recycled_content: m.recycled_content ?? null,
        voc_class: m.voc_class ?? null,
        sustainability_notes: m.sustainability_notes ?? null,
        carbon_footprint: m.carbon_footprint ?? null,
        epd: m.epd ?? null,
        vapor_permeability: m.vapor_permeability ?? null,
        thermal_capacity: m.thermal_capacity ?? null,

        mechanical_performance: m.mechanical_performance ?? null,
        thermal_performance_notes: m.thermal_performance_notes ?? null,
        system_compatibility: m.system_compatibility ?? null,

        color_hex: m.color_hex ?? null,
        intended_use: m.intended_use ?? null,
        compatible_board_types: m.compatible_board_types ?? null,
      };

      // Match per codice DENTRO LA PROPRIA ORG: dopo il passaggio a codice unico
      // per-org lo stesso codice può esistere sia nel catalogo globale sia in
      // un'org, quindi un lookup per solo codice tornerebbe più righe (e
      // `maybeSingle()` andrebbe in errore). L'import lavora sui materiali
      // dell'organizzazione corrente: UPDATE se già suo, altrimenti INSERT.
      if (!currentOrganizationId) {
        fail++;
        continue;
      }
      const { data: existingOwn } = await supabase
        .from('materials')
        .select('id')
        .eq('code', m.code)
        .eq('organization_id', currentOrganizationId)
        .maybeSingle();
      if (existingOwn) {
        const { error } = await supabase
          .from('materials')
          .update(inputMaterial as any)
          .eq('id', existingOwn.id);
        error ? fail++ : ok++;
      } else {
        const { error } = await supabase
          .from('materials')
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .insert([{ ...(inputMaterial as any), organization_id: currentOrganizationId }]);
        error ? fail++ : ok++;
      }
    }
    setImporting(false);
    setImportDialogOpen(false);
    setImportPreview([]);
    setImportErrors([]);
    if (ok > 0) {
      const familyMsg = familyMissing > 0
        ? ` Famiglie risolte: ${familyResolved}/${familyResolved + familyMissing} (${familyMissing} codici famiglia non trovati per quel fornitore).`
        : familyResolved > 0
          ? ` Famiglie risolte: ${familyResolved}.`
          : '';
      toast.success(`Import riuscito: ${ok} materiali salvati${fail ? `, ${fail} falliti` : ""}.${familyMsg}`);
      refetch();
    } else {
      toast.error("Nessun materiale importato con successo.");
    }
  };

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full">
        <AppSidebar />
        <SidebarInset className="flex flex-col">
          <Header />
          <main className="flex-1 p-6 md:p-8">
            <div className="mb-8">
              <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2" data-tour-id="materials-page">
                <span>
                  <Plus className="hidden" />
                </span>
                Gestione Materiali
              </h1>
              <p className="text-muted-foreground mt-1">
                Catalogo e gestione materiali per costruzioni a secco
              </p>
            </div>

            {/* Filtri e ricerca + Import/Export */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="text-lg">Filtri e Ricerca</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex-1">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Cerca per nome o codice (usa il filtro Fornitore →)…"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2 items-center">
                    <Button
                      variant={onlyFavorites ? 'default' : 'outline'}
                      size="default"
                      onClick={() => setOnlyFavorites(v => !v)}
                      className="gap-1.5"
                      title="Mostra solo i preferiti"
                    >
                      <Star className={`h-4 w-4 ${onlyFavorites ? 'fill-current' : ''}`} />
                      {onlyFavorites ? 'Solo preferiti' : 'Preferiti'}
                    </Button>
                    {hiddenCount > 0 && (
                      <Button
                        variant={showHidden ? 'default' : 'outline'}
                        size="default"
                        onClick={() => setShowHidden(v => !v)}
                        className="gap-1.5"
                        title="Mostra SOLO i materiali nascosti dal tuo catalogo (per ritrovarli e riattivarli)"
                      >
                        {showHidden ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                        {showHidden ? 'Solo nascosti' : 'Nascosti'} ({hiddenCount})
                      </Button>
                    )}
                    <Button
                      variant={groupByFormatOn ? 'default' : 'outline'}
                      size="default"
                      onClick={() => setGroupByFormatOn(v => !v)}
                      className="gap-1.5"
                      title="Raggruppa le varianti che differiscono solo per la lunghezza (stesso prezzo): mostra una riga sola per prodotto. Le varianti restano disponibili per la distinta materiali."
                    >
                      <Layers className="h-4 w-4" />
                      {groupByFormatOn ? 'Raggruppati' : 'Raggruppa formato'}
                    </Button>
                    <select
                      value={selectedCategory}
                      onChange={(e) => { setSelectedCategory(e.target.value as CategoryFilter); setActiveFacets({}); }}
                      className="px-3 py-2 border border-input rounded-md text-sm"
                    >
                      {materialCategoryOptions.map(category => (
                        <option key={category.value} value={category.value}>
                          {category.label}
                        </option>
                      ))}
                    </select>
                    <select
                      value={selectedSupplier}
                      onChange={(e) => setSelectedSupplier(e.target.value)}
                      className="px-3 py-2 border border-input rounded-md text-sm"
                      title="Filtra per fornitore"
                    >
                      <option value="all">Tutti i fornitori</option>
                      {supplierOptions.map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                    <select
                      value={selectedScope}
                      onChange={(e) => setSelectedScope(e.target.value as 'all' | 'global' | 'mine')}
                      className="px-3 py-2 border border-input rounded-md text-sm"
                      title="Filtra per scope: catalogo globale o materiali della tua organizzazione"
                    >
                      <option value="all">Tutti i materiali</option>
                      <option value="global">Solo catalogo globale</option>
                      <option value="mine" disabled={!currentOrganizationId}>Solo miei (org)</option>
                    </select>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" className="flex items-center gap-1">
                          Importa/Esporta <ChevronDown className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent>
                        <DropdownMenuItem onClick={() => setCustomWizardOpen(true)}>
                          ✏️ Importa i tuoi prezzi/tempi (wizard)
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={handleExportVisible} disabled={filteredMaterials.length === 0}>
                          Esporta visibili ({filteredMaterials.length}) (.xlsx)
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={handleExportSelected} disabled={selectedIds.size === 0}>
                          Esporta selezionati ({selectedIds.size}) (.xlsx)
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={handleExportAll}>
                          Esporta TUTTO il catalogo ({materials.length}) (.xlsx)
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onSelect={() => setImportDialogOpen(true)}
                        >
                          Importa materiali da Excel
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <Button variant="outline" asChild>
                      <Link to="/materials/import">
                        <Upload className="h-4 w-4 mr-2" />
                        Importa listino
                      </Link>
                    </Button>
                    <Button onClick={() => setIsCreateDialogOpen(true)}>
                      <Plus className="h-4 w-4 mr-2" />
                      Nuovo Materiale
                    </Button>
                  </div>
                </div>
                
                {/* Faccette della categoria scelta — le stesse del picker del
                    configuratore, cosi' i due elenchi si filtrano allo stesso modo.
                    I conteggi escludono la faccetta stessa: dicono quanti materiali
                    resterebbero cliccando quel chip. */}
                {facets.length > 0 && (
                  <div className="mt-3 border-t pt-3 space-y-1.5">
                    {facets.map(facet => {
                      const selected = activeFacets[facet.key] ?? [];
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      const pool = (facetCountBase as any[]).filter(m => matchesFacets(m, facets, activeFacets, facet.key));
                      const visible = facet.options
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        .map(opt => ({ opt, count: (pool as any[]).filter(m => facet.valuesOf(m).includes(opt.value)).length }))
                        .filter(o => o.count > 0);
                      if (visible.length === 0) return null;
                      return (
                        <div key={facet.key} className="flex flex-wrap gap-1 items-center">
                          <span className="text-[10px] text-muted-foreground uppercase tracking-wide w-24 shrink-0">
                            {facet.label}
                          </span>
                          {selected.length > 0 && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-6 text-[11px] px-2 text-muted-foreground"
                              onClick={() => setActiveFacets(prev => { const o = { ...prev }; delete o[facet.key]; return o; })}
                              title={`Azzera ${facet.label.toLowerCase()}`}
                            >
                              ✕
                            </Button>
                          )}
                          {visible.map(({ opt, count }) => (
                            <Button
                              key={opt.value}
                              size="sm"
                              variant={selected.includes(opt.value) ? 'secondary' : 'ghost'}
                              className="h-6 text-[11px] px-2"
                              onClick={() => toggleFacetValue(facet.key, opt.value)}
                            >
                              {opt.label} <span className="ml-1 opacity-70">{count}</span>
                            </Button>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                )}

                {(searchTerm || selectedCategory !== 'all' || selectedSupplier !== 'all' || selectedScope !== 'all') && (
                  <div className="mt-4 flex items-center gap-2 flex-wrap">
                    <span className="text-sm text-muted-foreground">
                      {filteredMaterials.length} materiali trovati
                    </span>
                    {searchTerm && (
                      <Badge variant="secondary">
                        Ricerca: "{searchTerm}"
                      </Badge>
                    )}
                    {selectedCategory !== 'all' && (
                      <Badge variant="secondary">
                        Categoria: {materialCategoryOptions.find(c => c.value === selectedCategory)?.label}
                      </Badge>
                    )}
                    {selectedSupplier !== 'all' && (
                      <Badge variant="secondary">
                        Fornitore: {selectedSupplier}
                      </Badge>
                    )}
                    {selectedScope !== 'all' && (
                      <Badge variant="secondary">
                        Scope: {selectedScope === 'global' ? 'Catalogo globale' : 'Miei (org)'}
                      </Badge>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Riepilogo risparmio (solo se ci sono materiali con sconto) */}
            {(() => {
              const withDiscount = filteredMaterials.filter(m =>
                m.list_price != null && m.list_price > (m.unit_price ?? 0) + 0.001
              );
              if (withDiscount.length === 0) return null;
              const totalList = withDiscount.reduce((s, m) => s + (m.list_price ?? 0), 0);
              const totalNet  = withDiscount.reduce((s, m) => s + (m.unit_price ?? 0), 0);
              const savePct = totalList > 0 ? ((1 - totalNet / totalList) * 100) : 0;
              return (
                <Card className="mb-6 bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
                  <CardContent className="pt-6">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <Stat label="Materiali con sconto" value={`${withDiscount.length} / ${filteredMaterials.length}`} />
                      <Stat label="Totale listino" value={`€ ${formatNumber(totalList)}`} />
                      <Stat label="Totale netto" value={`€ ${formatNumber(totalNet)}`} accent="text-green-700" />
                      <Stat label="Risparmio medio" value={`${formatNumber(savePct, 1)}%`} accent="text-green-700" />
                    </div>
                  </CardContent>
                </Card>
              );
            })()}

            {/* Tabella materiali */}
            <Card>
              <CardHeader>
                <CardTitle>
                  Catalogo Materiali ({displayMaterials.length}{groupByFormatOn ? ' · raggruppati per formato' : ''})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="flex items-center justify-center p-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mr-4"></div>
                    <span>Caricamento materiali...</span>
                  </div>
                ) : (
                  <>
                    {selectedIds.size > 0 && (
                      <div className="mb-3 flex items-center justify-between gap-3 rounded-md border border-primary/30 bg-primary/5 px-3 py-2">
                        <div className="text-sm flex items-center gap-2">
                          <strong className="tabular-nums">{selectedIds.size}</strong>
                          {selectedIds.size === 1 ? 'materiale selezionato' : 'materiali selezionati'}
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            onClick={() => setBulkDialogOpen(true)}
                            className="gap-1.5"
                          >
                            <TrendingUp className="h-4 w-4" />
                            Modifica prezzi in massa
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setSelectedIds(new Set())}
                            className="gap-1.5"
                            title="Deseleziona tutti"
                          >
                            <XIcon className="h-4 w-4" />
                            Annulla
                          </Button>
                        </div>
                      </div>
                    )}
                    <MaterialsTable
                      materials={pagedMaterials}
                      formatGroups={formatGroups}
                      onEditMaterial={setEditingMaterial}
                      sortField={sortField}
                      sortDirection={sortDirection}
                      onSortChange={setSort}
                      selectedIds={selectedIds}
                      onToggleSelect={(id) => {
                        setSelectedIds((prev) => {
                          const next = new Set(prev);
                          if (next.has(id)) next.delete(id);
                          else next.add(id);
                          return next;
                        });
                      }}
                      onToggleSelectAll={() => {
                        // "Seleziona tutti" = tutti i visibili nella pagina corrente.
                        // Se ti servono TUTTI i filtrati (anche su altre pagine), il
                        // toggle scatta solo sulla pagina; per il bulk "tutti" usa
                        // la bulkbar che gia' lavora su selectedIds totali.
                        setSelectedIds((prev) => {
                          const allVisibleSelected = pagedMaterials.length > 0 &&
                            pagedMaterials.every((m) => prev.has(m.id));
                          if (allVisibleSelected) {
                            const next = new Set(prev);
                            for (const m of pagedMaterials) next.delete(m.id);
                            return next;
                          }
                          const next = new Set(prev);
                          for (const m of pagedMaterials) next.add(m.id);
                          return next;
                        });
                      }}
                    />

                    {/* Footer paginazione: visibile solo se ci sono > 1 pagine. */}
                    {totalPages > 1 && (
                      <div className="flex items-center justify-between gap-2 px-2 py-3 border-t bg-zinc-50/50">
                        <div className="text-xs text-muted-foreground">
                          Mostro {safePageIndex * PAGE_SIZE + 1}–{Math.min((safePageIndex + 1) * PAGE_SIZE, sortedMaterials.length)} di {sortedMaterials.length} materiali
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setPageIndex((p) => Math.max(0, p - 1))}
                            disabled={safePageIndex === 0}
                            className="h-8 px-2"
                          >
                            ← Prec
                          </Button>
                          <span className="text-xs font-medium px-2 tabular-nums">
                            Pagina {safePageIndex + 1} di {totalPages}
                          </span>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setPageIndex((p) => Math.min(totalPages - 1, p + 1))}
                            disabled={safePageIndex >= totalPages - 1}
                            className="h-8 px-2"
                          >
                            Succ →
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            {/* Dialogs */}
            <CreateMaterialDialog 
              open={isCreateDialogOpen}
              onOpenChange={setIsCreateDialogOpen}
              onSuccess={handleMaterialCreated}
            />

            {editingMaterial && (
              <EditMaterialDialog
                material={editingMaterial}
                open={!!editingMaterial}
                onOpenChange={(open) => !open && setEditingMaterial(null)}
                onSuccess={handleMaterialUpdated}
              />
            )}

            <BulkPriceEditDialog
              open={bulkDialogOpen}
              onOpenChange={setBulkDialogOpen}
              selectedIds={Array.from(selectedIds)}
              onSuccess={() => {
                setSelectedIds(new Set());
                refetch();
              }}
            />

            {/* Dialog import Excel */}
            {importDialogOpen && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
                <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-xl relative">
                  <button
                    className="absolute right-3 top-3 text-xl"
                    onClick={() => {
                      setImportDialogOpen(false);
                      setImportPreview([]);
                      setImportErrors([]);
                    }}
                  >
                    ×
                  </button>
                  <h2 className="text-lg font-semibold mb-2">Importa materiali da Excel</h2>
                  <label className="block mb-2">Seleziona file Excel (.xlsx):</label>
                  <input
                    type="file"
                    accept=".xlsx"
                    className="mb-4"
                    onChange={handleImportFile}
                  />
                  {importErrors.length > 0 && (
                    <div className="mb-2 text-sm text-red-600">
                      {importErrors.map(err => (
                        <div key={err}>{err}</div>
                      ))}
                    </div>
                  )}
                  {importPreview.length > 0 && (
                    <div className="mb-4 max-h-52 overflow-auto">
                      <table className="w-full text-xs border rounded">
                        <thead>
                          <tr className="bg-gray-100">
                            <th>Codice</th>
                            <th>Nome</th>
                            <th>Categoria</th>
                            <th>Fornitore</th>
                            <th>PrezzoUn</th>
                          </tr>
                        </thead>
                        <tbody>
                          {importPreview.map((m, i) => (
                            <tr key={i} className="border-t">
                              <td>{m.code}</td>
                              <td>{m.name}</td>
                              <td>{m.category}</td>
                              <td>{m.supplier}</td>
                              <td>{m.unit_price}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setImportDialogOpen(false);
                        setImportPreview([]);
                        setImportErrors([]);
                      }}
                      disabled={importing}
                    >
                      Annulla
                    </Button>
                    <Button
                      onClick={handleSaveImport}
                      disabled={importing || importPreview.length === 0}
                    >
                      {importing ? "Importazione..." : "Importa e Salva"}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            <ImportCustomizationsWizard
              open={customWizardOpen}
              onOpenChange={setCustomWizardOpen}
              materials={materials}
            />
          </main>
        </SidebarInset>
      </div>
    </SidebarProvider>
  );
};

export default Materials;
